import { DateTime } from 'luxon'
import React, { Fragment, useEffect, useState } from 'react'
import { Row, Col } from 'react-grid-system'
import { useTranslation } from 'react-i18next'

import { excludedStatuses } from './OrderDetail.model'
import {
  StyledOrderDetail,
  StyledProductDetailsSection,
  StyledActions,
  StyledClinSecondaryButton,
  StyledLinkButton,
  StyledClinPrimaryButton,
  StyledHeaderClinPageContentFrame,
  StyledOrderDetailHeaderContainer,
  StyledOrderDetailHeaderNavigation,
  StyledOrderDetailHeaderContent,
  StyledOrderDetailHeaderTitle,
  StyledOrderDetailDeliveryDateContainer,
  StyledOrderDetailDeliveryDateIconContainer,
  StyledOrderDetailDeliveryDateContent,
  StyledOrderDetailEstimatedDeliveryDateButton,
  OrderDetailHeaderContainer,
  StyledButtonLabel
} from './OrderDetail.styles'
import { ClinTheme } from '../../../ClinTheme'
import { ClinToastBar } from '../../../components/ClinToastBar/ClinToastBar'
import {
  ClinAnnounceBar,
  IClinAnnounceBarProps,
  AnnounceMode
} from '../../../components/ClinAnnounceBar/ClinAnnounceBar'
import { ClinButton } from '../../../components/ClinButton'
import { ClinIcon } from '../../../components/ClinIcon'
import { ClinIconPathName } from '../../../components/ClinIcon/ClinIcon.paths'
import { INewFeature } from '../../../components/ClinNewFeatureTooltip/ClinNewFeatureTooltip.types'
import { ClinPageContentFrame } from '../../../components/ClinPageContentFrame'
import { ClinSpacer } from '../../../components/ClinSpacer'
import { ClinSpinner } from '../../../components/ClinSpinner'
import { StyledSpinnerContainer } from '../../../components/ClinSpinner/ClinSpinner.styles'
import { ClinText } from '../../../components/ClinText'
import { TypographyVariant } from '../../../components/ClinText/ClinText.styles'
import i18n from '../../../i18n/config'
import analyticsServiceSingleton from '../../../services/Analytics/initAnalytics'
import { getVariantInformationForCatalogItem } from '../../../services/PortfolioJourneys/PortfolioJourney.model'
import { IAugmentedCatalogItemDto } from '../../../services/PortfolioJourneys/PortfolioJourney.types'
import { OrderStatus } from '../../../types/OrderStatus'
import {
  HoldDto,
  OrderDto,
  OrderLineDto,
  UploadedDocumentDto
} from '../../../types/swaggerTypes'
import { getBrowserLocale } from '../../../utils/getBrowserLocale'
import { numberToCurrencyString } from '../../../utils/numberToCurrencyString'
import {
  FixedWidthCell,
  ProductInformationBox,
  StyledClinPatientTableHeaderCell,
  StyledClinTable,
  StyledClinTableBodyCell,
  StyledClinTableExpandedRow,
  StyledClinTableRow,
  StyledClinTableToggleRowButton,
  StyledDesktopWrapper,
  StyledProductInformationBox,
  StyledStatusCell,
  StyledStrengthCell
} from './OrderProductDetails.styles'
import { StyledClinTableOrderToggleWrapper } from '../../Patients/PatientDashboard/PatientDashboard.styles'
import { ClinTableOrderToggle } from '../../../components/ClinTableOrderToggle'
import { ClinTableBodyCell } from '../../../components/ClinTable/ClinTable'
import StatusColumn from '../Orders/StatusColumn/StatusColumn'
import { getOrderById } from '../../../services/ApiService'
import ProductInformation from './ProductInformation/ProductInformation'
import { useHistory } from 'react-router'
import { Tooltip } from 'react-tippy'
import { compareTwoDates } from '../../../utils/dateUtils'
import OrderDelivery from './OrderDelivery/OrderDelivery'
import OrderSummary from './OrderSummary/OrderSummary'
import { isOrderEligibleForInvoice } from '../../../utils/orderUtils'

interface IOrderDetailProps {
  /** A placed order **/
  order?: OrderDto
  /** List of new features for this page **/
  newFeaturesList?: { [id: string]: INewFeature }
  /** Whether we are loading or not **/
  isLoading: boolean
  /** Country code **/
  countryCode: string
  /** Whether we are adding something to basket **/
  isAddingToBasket: boolean
  /** When drawer is loading **/
  isDrawerLoading: boolean
  /** When download invoice **/
  isDownloadingInvoice: boolean
  /** Displays an announce at the top of a page **/
  orderAnnounce?: IClinAnnounceBarProps
  /** Whether all ordered items can be requested again **/
  canAllItemsBeOrdered?: boolean
  /** Back button title */
  backButtonTitle?: string
  /** Request to print order **/
  onPrint?: () => void
  /** Request order again **/
  onRequestAgain?: () => void
  /** Request to go back to a page **/
  onBack?: () => void
  /** Handles cancelling an order line for an order **/
  onCancelOrderLine?: (orderLine: OrderLineDto) => void
  /** Handles adding an order line to the basket **/
  onAddToBasket?: (orderLine: OrderLineDto, quantity: number) => void
  /** Handles a request to email order details **/
  onEmailOrderDetails?: () => void
  /** Handles the dismissal of the announcement bar **/
  handleDismissAnnounce?: () => void
  /** Called when a file has been uploaded */
  handleFileUploadedForOrderAndHold?: (
    orderId: string,
    orderLine: OrderLineDto,
    hold: HoldDto,
    document: UploadedDocumentDto
  ) => void
  /** Call back when file is deleted for a hold */
  handleFileDeleteForOrderAndHold?: (
    orderLine: OrderLineDto,
    hold: HoldDto,
    documentId?: number,
    dataPlatformDocumentId?: string
  ) => void
  /** Handles the dismissal of the announcement bar **/
  handleTrackingLinkClicked: (orderLine: OrderLineDto, language: string) => void
  getCarrierType: (carrier: string | undefined) => string
}

const handleTrackOrder = async (orderNumber: string) => {
  try {
    const response = await getOrderById(orderNumber)
    const orderDto: OrderDto = response.data as OrderDto

    const orderLineWithTrackingLink = orderDto.lines.find(
      (line) => line.trackingInfo?.aftershipTrackingLink !== null
    )
    const trackingLink =
      orderLineWithTrackingLink?.trackingInfo?.aftershipTrackingLink
    if (trackingLink) {
      window.open(trackingLink, '_blank')
    }
  } catch (error: any) {
    analyticsServiceSingleton.trackError(
      error,
      JSON.parse(localStorage.getItem('current_user') || '{}')
    )
  }
}

export const OrderDetail: React.FC<IOrderDetailProps> = ({
  order,
  isLoading,
  countryCode,
  isAddingToBasket,
  isDrawerLoading,
  isDownloadingInvoice,
  canAllItemsBeOrdered,
  orderAnnounce,
  backButtonTitle,
  onRequestAgain,
  onBack,
  onCancelOrderLine,
  onAddToBasket,
  onEmailOrderDetails,
  handleDismissAnnounce,
  handleFileUploadedForOrderAndHold,
  handleFileDeleteForOrderAndHold,
  handleTrackingLinkClicked,
  getCarrierType
}) => {
  const history = useHistory()
  const { t } = useTranslation()
  /**
   * Calculate what are medication line items and what are additional line items
   * */
  // const medications: OrderLineDto[] = getMedications(order)

  const [expandedRows, setExpandedRows] = useState<string[]>([])
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)

  const hasAftershipTracking = (orderLine: OrderLineDto): boolean => {
    return !!(
      orderLine.trackingInfo?.waybillNumber &&
      getCarrierType(orderLine.trackingInfo?.freightCarrier) === 'Aftership'
    )
  }

  const showTrackingInfo = (orderLine: OrderLineDto): React.ReactNode => {
    if (hasAftershipTracking(orderLine)) {
      return orderLine.trackingInfo?.aftershipTrackingLink
        ? t('orders:actions.track_order')
        : t('orders:actions.tracking_link_pending')
    }
    return t('orders:actions.tracking_link_pending')
  }

  const isInvoiceAvailable = isOrderEligibleForInvoice(order)

  const handleSelectProduct = (orderLine: OrderLineDto) => {
    history.push(`/product/sku/${orderLine.skuCatalogItem?.sku}`)
  }

  const cannotOrder = (orderLine: OrderLineDto) => {
    const variantInformation: IAugmentedCatalogItemDto | null =
      orderLine.skuCatalogItem &&
      getVariantInformationForCatalogItem(orderLine.skuCatalogItem, countryCode)

    return variantInformation?.cannotOrder
  }

  const getProductDetailsColumns = (order: OrderDto) => [
    {
      name: 'strength',
      viewName: 'Strength',
      translationKey: 'glossary:strength',
      width: '100%',
      minWidth: '145px',
      maxWidth: '165px',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => (
        <>
          {orderLine.skuCatalogItem?.products.map((product: any) => (
            <StyledStrengthCell key={`strength-item-${product.genericName}`}>
              <ClinText
                variant={TypographyVariant.SmallUI}
                lineHeight={ClinTheme.lineHeights.body}
                fontSize={ClinTheme.fontSizes[1]}
              >
                {product.genericName}
              </ClinText>
              <ClinText
                lineHeight={ClinTheme.lineHeights.body}
                fontSize={ClinTheme.fontSizes[1]}
              >
                {product.strength}
              </ClinText>
            </StyledStrengthCell>
          ))}
        </>
      )
    },
    {
      name: 'dosageFormAndPackSize',
      viewName: 'Dosage form and pack size',
      translationKey: 'glossary:dosage_and_packsize',
      width: '100%',
      minWidth: '115px',
      maxWidth: '180px',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => (
        <>
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.body}
          >
            {orderLine.skuCatalogItem?.item.formulation}
          </ClinText>
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.body}
          >
            {orderLine.skuCatalogItem?.item.packSize}
          </ClinText>
        </>
      )
    },
    {
      name: 'status',
      viewName: 'Status',
      translationKey: 'glossary:status',
      width: '100%',
      minWidth: '158px',
      maxWidth: '168px',
      renderValue: (orderLine: OrderLineDto) => {
        return (
          <StyledStatusCell>
            <StatusColumn orderStatus={orderLine.status ?? order.orderStatus} />
          </StyledStatusCell>
        )
      }
    },
    {
      name: 'price',
      viewName: 'Pack price',
      translationKey: 'glossary:pack_price',
      width: '100%',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => {
        return (
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.body}
          >
            {numberToCurrencyString(
              orderLine.unitSellingPrice,
              getBrowserLocale(),
              {
                style: 'currency',
                currency: order?.currencyCode ? order?.currencyCode : 'GBP',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }
            ) ?? '--'}
          </ClinText>
        )
      }
    },
    {
      name: 'quantity',
      viewName: 'Quantity',
      translationKey: 'order_detail:quantity',
      width: '100%',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => {
        return (
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.body}
          >
            {orderLine.quantities.ordered ?? '--'}
          </ClinText>
        )
      }
    },
    {
      name: 'total',
      viewName: 'Total',
      translationKey: 'order_detail:total_cell',
      width: '100%',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => {
        return (
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.body}
            fontWeight={ClinTheme.fontWeights.bold}
          >
            {numberToCurrencyString(
              orderLine.totals.subTotal,
              getBrowserLocale(),
              {
                style: 'currency',
                currency: order?.currencyCode ? order?.currencyCode : 'GBP',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }
            ) ?? '--'}
          </ClinText>
        )
      }
    },
    {
      name: 'toggle',
      viewName: 'Actions',
      translationKey: 'order_detail:actions',
      width: '100%',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => (
        <StyledActions onClick={(e) => e.stopPropagation()}>
          <StyledClinSecondaryButton
            variant="secondary"
            disabled={
              isAddingToBasket || isDrawerLoading || cannotOrder(orderLine)
            }
            onClick={() =>
              orderLine.skuCatalogItem?.sku &&
              onAddToBasket &&
              onAddToBasket(orderLine, orderLine.quantities.ordered)
            }
          >
            {t('order_detail:order_again')}
          </StyledClinSecondaryButton>
          {!excludedStatuses.includes(orderLine.status as OrderStatus) && (
            <StyledLinkButton
              variant="link"
              disabled={isAddingToBasket || isDrawerLoading}
              onClick={() => onCancelOrderLine && onCancelOrderLine(orderLine)}
            >
              {t('order_detail:cancel')}
            </StyledLinkButton>
          )}
          <StyledClinTableToggleRowButton
            isToggled={expandedRows.includes(
              `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
            )}
            onClick={() => {
              handleToggleRow &&
                handleToggleRow(
                  `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                )
            }}
          />
        </StyledActions>
      )
    }
  ]

  const handleToggleRow = (itemId: string) => {
    setExpandedRows((prev: string[]) =>
      prev.includes(itemId)
        ? prev.filter((id: string) => id !== itemId)
        : [...prev, itemId]
    )
  }

  const handleMouseEnter = (id: string) => {
    setHoveredRow(id)
  }

  const handleMouseLeave = () => {
    setHoveredRow(null)
  }

  const getLatestDeliveryDate = (orderLine: OrderLineDto): string | null => {
    let latestDate: string | null = null
    if (orderLine.trackingInfo?.estimatedDeliveryDate) {
      latestDate = orderLine.trackingInfo.estimatedDeliveryDate
    }
    if (orderLine.expectedDate && !latestDate) {
      latestDate = orderLine.expectedDate
    }

    return latestDate
  }

  const formatDeliveryDate = (
    dateString: string | null,
    language: string
  ): React.ReactNode => {
    if (!dateString) {
      return t('glossary:not_available')
    }

    return DateTime.fromISO(dateString)
      .setLocale(language)
      .toFormat('cccc, MMM d')
  }

  const getLatestDeliveryDateFromAllOrderLines = (): string | null => {
    if (!order || !order.lines) return null
    let latestDate: string | null = null
    order.lines
      .filter((ol) => ol.medicationFlag === 'Y')
      .forEach((orderLine) => {
        const lineLatestDate = getLatestDeliveryDate(orderLine)

        if (lineLatestDate) {
          if (!latestDate) {
            latestDate = lineLatestDate
          } else {
            latestDate = compareTwoDates(latestDate, lineLatestDate)
          }
        }
      })

    return latestDate
  }

  const getOrderLineWithLatestDeliveryDate = (): OrderLineDto | null => {
    if (!order || !order.lines) return null

    const latestDate = getLatestDeliveryDateFromAllOrderLines()
    if (!latestDate) return null

    return (
      order.lines
        .filter((ol) => ol.medicationFlag === 'Y')
        .find((orderLine) => {
          const lineLatestDate = getLatestDeliveryDate(orderLine)
          return lineLatestDate === latestDate
        }) || null
    )
  }

  useEffect(() => {
    const orderLines = order?.lines?.filter((ol) => ol.medicationFlag === 'Y')
    if (orderLines?.length === 1 && expandedRows.length === 0) {
      const orderLine = orderLines[0]
      handleToggleRow(`${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`)
    }
  }, [order])

  if (!order) return null
  const columns = getProductDetailsColumns(order)
  return (
    <ClinPageContentFrame isFullWidth={true}>
      {isLoading && (
        <Row justify="center">
          <Col width="auto">
            <StyledSpinnerContainer>
              <ClinSpinner size={ClinTheme.space[7]} />
            </StyledSpinnerContainer>
          </Col>
        </Row>
      )}

      {!isLoading && order && order.lines && (
        <ClinPageContentFrame isFullWidth={true}>
          <StyledOrderDetail>
            <OrderDetailHeaderContainer className="order-detail-header">
              <StyledHeaderClinPageContentFrame>
                <StyledOrderDetailHeaderContainer>
                  <StyledOrderDetailHeaderNavigation>
                    {onBack && backButtonTitle && (
                      <ClinButton
                        onClick={() => onBack()}
                        variant="linkWithIcon"
                      >
                        <ClinIcon
                          style={{ marginRight: ClinTheme.space[2] }}
                          iconSize={ClinTheme.fontSizes[3]}
                          iconName={ClinIconPathName.ArrowLeft}
                        />
                        {backButtonTitle}
                      </ClinButton>
                    )}
                    <StyledOrderDetailHeaderTitle
                      marginTop={ClinTheme.space[1]}
                      marginBottom={ClinTheme.space[1]}
                      variant={TypographyVariant.H3}
                    >
                      {t('order_detail:order_number', {
                        orderNumber: order.orderNumber
                      })}
                    </StyledOrderDetailHeaderTitle>
                  </StyledOrderDetailHeaderNavigation>
                  <StyledOrderDetailHeaderContent>
                    <Tooltip
                      theme="light"
                      position="bottom"
                      trigger="mouseenter"
                      arrow={true}
                      disabled={isInvoiceAvailable || isDownloadingInvoice}
                      title={
                        isInvoiceAvailable || isDownloadingInvoice
                          ? ''
                          : t('order_detail:invoice_tooltip')
                      }
                    >
                      <ClinButton
                        className="download-invoice-button"
                        variant="primary"
                        disabled={!isInvoiceAvailable || isDownloadingInvoice}
                        onClick={() =>
                          onEmailOrderDetails && onEmailOrderDetails()
                        }
                      >
                        {isDownloadingInvoice && (
                          <ClinSpinner size={ClinTheme.space[3]} />
                        )}
                        <StyledButtonLabel>
                          {t('order_detail:download_invoice')}
                        </StyledButtonLabel>
                      </ClinButton>
                    </Tooltip>
                    {(() => {
                      const latestDate =
                        getLatestDeliveryDateFromAllOrderLines()
                      const orderLineWithLatestDate =
                        getOrderLineWithLatestDeliveryDate()
                      const shouldShowDeliveryDate =
                        orderLineWithLatestDate &&
                        latestDate &&
                        order?.orderStatus !== OrderStatus.Cancelled &&
                        order?.orderStatus !== OrderStatus.Shipped

                      if (!shouldShowDeliveryDate) {
                        return null
                      }

                      return (
                        <StyledOrderDetailDeliveryDateContainer>
                          <StyledOrderDetailDeliveryDateIconContainer>
                            <ClinIcon
                              viewBox="0 0 37 35"
                              iconHeight={'32px'}
                              iconSize={'32px'}
                              iconName={ClinIconPathName.DeliveryBox}
                              iconFill={ClinTheme.colors.primaryMid}
                            />
                          </StyledOrderDetailDeliveryDateIconContainer>
                          <StyledOrderDetailDeliveryDateContent>
                            <Fragment>
                              <ClinText
                                fontSize={ClinTheme.fontSizes[0]}
                                lineHeight={'16px'}
                                fontWeight={ClinTheme.fontWeights.medium}
                                color={ClinTheme.colors.primaryMid}
                              >
                                {order.orderStatus === OrderStatus.Delivered
                                  ? t('order_detail:delivered')
                                  : t('order_detail:estimated_delivery')}
                              </ClinText>
                              <ClinText
                                fontSize={ClinTheme.fontSizes[1]}
                                fontWeight={ClinTheme.fontWeights.medium}
                                lineHeight={'24px'}
                                color={ClinTheme.colors.primaryMid}
                              >
                                {formatDeliveryDate(latestDate, i18n.language)}
                              </ClinText>
                              <Tooltip
                                theme="light"
                                position="bottom"
                                trigger="mouseenter"
                                arrow={true}
                                disabled={
                                  hasAftershipTracking(
                                    orderLineWithLatestDate
                                  ) &&
                                  !!orderLineWithLatestDate.trackingInfo
                                    ?.aftershipTrackingLink
                                }
                                title={t('order_detail:tracking_tooltip')}
                              >
                                <StyledOrderDetailEstimatedDeliveryDateButton
                                  className="tracking-link-button"
                                  variant={
                                    hasAftershipTracking(
                                      orderLineWithLatestDate
                                    ) &&
                                    !!orderLineWithLatestDate.trackingInfo
                                      ?.aftershipTrackingLink
                                      ? 'link'
                                      : 'linkButton'
                                  }
                                  color={ClinTheme.colors.primaryMid}
                                  disabled={false}
                                  onClick={() => {
                                    order?.orderNumber &&
                                      handleTrackOrder(order.orderNumber)
                                    handleTrackingLinkClicked(
                                      orderLineWithLatestDate,
                                      i18n.language
                                    )
                                  }}
                                >
                                  {showTrackingInfo(orderLineWithLatestDate)}
                                </StyledOrderDetailEstimatedDeliveryDateButton>
                              </Tooltip>
                            </Fragment>
                          </StyledOrderDetailDeliveryDateContent>
                        </StyledOrderDetailDeliveryDateContainer>
                      )
                    })()}
                  </StyledOrderDetailHeaderContent>
                </StyledOrderDetailHeaderContainer>
              </StyledHeaderClinPageContentFrame>
            </OrderDetailHeaderContainer>
            <ClinPageContentFrame>
              {orderAnnounce && (
                <>
                  {orderAnnounce.mode === AnnounceMode.Success ? (
                    <ClinToastBar
                      handleClose={handleDismissAnnounce}
                      mode={orderAnnounce.mode}
                      title={orderAnnounce.title}
                      isDismissibleText={orderAnnounce.isDismissibleText}
                    >
                      {orderAnnounce.message}
                    </ClinToastBar>
                  ) : (
                    <ClinAnnounceBar
                      handleClose={handleDismissAnnounce}
                      mode={orderAnnounce.mode}
                      title={orderAnnounce.title}
                    >
                      {orderAnnounce.message}
                    </ClinAnnounceBar>
                  )}
                  <ClinSpacer height={ClinTheme.space[4]} />
                </>
              )}
              <Row onClick={(e) => e.stopPropagation()}>
                <Col id="order-detail-documents-announce-bar-container" />
              </Row>
              <Row>
                <Col>
                  <OrderDelivery order={order} />
                </Col>
                <Col
                  style={{
                    maxWidth: '360px',
                    paddingLeft: ClinTheme.space[2],
                    boxSizing: 'content-box'
                  }}
                >
                  <OrderSummary order={order} />
                </Col>
              </Row>
              <ClinSpacer height={ClinTheme.space[4]} />

              <StyledProductDetailsSection>
                <ClinText
                  as="span"
                  color={ClinTheme.colors.black}
                  fontWeight={ClinTheme.fontWeights.bold}
                  fontSize={ClinTheme.fontSizes[1]}
                >
                  {t('order_detail:product_details')}
                </ClinText>
                <StyledClinPrimaryButton
                  variant="primary"
                  disabled={
                    isAddingToBasket || isDrawerLoading || !canAllItemsBeOrdered
                  }
                  onClick={() => onRequestAgain && onRequestAgain()}
                >
                  {t('order_detail:order_available_items')}
                </StyledClinPrimaryButton>
              </StyledProductDetailsSection>
              <StyledDesktopWrapper>
                <StyledClinTable
                  borderColapseSeparate={true}
                  className="TableOverflowVisible"
                  tableHeader={() =>
                    columns.map((column) => (
                      <StyledClinPatientTableHeaderCell
                        key={`th-${column.name}`}
                        background={ClinTheme.colors.lightGrey}
                      >
                        <FixedWidthCell
                          width={column.width}
                          minWidth={column.minWidth}
                          maxWidth={column.maxWidth}
                        >
                          {column.isSortable ? (
                            <StyledClinTableOrderToggleWrapper>
                              <ClinTableOrderToggle isUppercase={false}>
                                {column.viewName}
                              </ClinTableOrderToggle>
                            </StyledClinTableOrderToggleWrapper>
                          ) : (
                            <StyledClinTableOrderToggleWrapper>
                              <ClinTableOrderToggle hideSortIcon={true}>
                                {column.viewName}
                              </ClinTableOrderToggle>
                            </StyledClinTableOrderToggleWrapper>
                          )}
                        </FixedWidthCell>
                      </StyledClinPatientTableHeaderCell>
                    ))
                  }
                >
                  {order.lines
                    ?.filter((ol) => ol.medicationFlag === 'Y')
                    ?.map((orderLine: OrderLineDto, index: number) => (
                      <Fragment
                        key={`variant-row-${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}-${index}`}
                      >
                        {/* Main Row */}
                        <StyledClinTableRow
                          background={ClinTheme.colors.white}
                          className={`main-row ${
                            hoveredRow ===
                            `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                              ? 'highlighted'
                              : ''
                          } ${
                            expandedRows.includes(
                              `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                            )
                              ? 'expanded'
                              : ''
                          }`}
                          onMouseEnter={() =>
                            handleMouseEnter(
                              `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                            )
                          }
                          onMouseLeave={handleMouseLeave}
                        >
                          {columns.map((column) => (
                            <StyledClinTableBodyCell
                              key={`${column.name}-${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`}
                              rowExpanded={expandedRows.includes(
                                `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                              )}
                              onClick={() =>
                                handleSelectProduct &&
                                handleSelectProduct(orderLine)
                              }
                            >
                              <FixedWidthCell
                                width={column.width}
                                minWidth={column.minWidth}
                                maxWidth={column.maxWidth}
                              >
                                {column.renderValue
                                  ? column.renderValue(orderLine)
                                  : null}
                              </FixedWidthCell>
                            </StyledClinTableBodyCell>
                          ))}
                        </StyledClinTableRow>

                        {/* Expanded Row */}
                        <StyledClinTableExpandedRow
                          background={ClinTheme.colors.white}
                          isOpen={expandedRows.includes(
                            `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                          )}
                          className={`expanded-row ${
                            hoveredRow ===
                            `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                              ? 'highlighted'
                              : ''
                          }`}
                          onMouseEnter={() =>
                            handleMouseEnter(
                              `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                            )
                          }
                          onMouseLeave={handleMouseLeave}
                        >
                          <StyledClinTableBodyCell
                            colSpan={10}
                            style={{ padding: '16px' }}
                            onClick={() =>
                              handleSelectProduct &&
                              handleSelectProduct(orderLine)
                            }
                          >
                            <StyledProductInformationBox>
                              <ProductInformationBox>
                                <ProductInformation
                                  orderStatus={order?.orderStatus}
                                  orderLines={order?.lines}
                                  orderId={order?.orderNumber}
                                  orderLine={orderLine}
                                  handleFileUploadedForOrderAndHold={
                                    handleFileUploadedForOrderAndHold
                                  }
                                  handleFileDeleteForOrderAndHold={
                                    handleFileDeleteForOrderAndHold
                                  }
                                />
                              </ProductInformationBox>
                            </StyledProductInformationBox>
                          </StyledClinTableBodyCell>
                        </StyledClinTableExpandedRow>

                        {/* Empty Row */}
                        <StyledClinTableRow>
                          <ClinTableBodyCell
                            colSpan={10}
                            style={{ padding: '4px' }}
                          ></ClinTableBodyCell>
                        </StyledClinTableRow>
                      </Fragment>
                    ))}
                </StyledClinTable>
              </StyledDesktopWrapper>
            </ClinPageContentFrame>
          </StyledOrderDetail>
        </ClinPageContentFrame>
      )}
    </ClinPageContentFrame>
  )
}
